import * as RadixPopover from "@radix-ui/react-popover";
import clsx from "clsx";
import { Command } from "cmdk";
import { ReactNode } from "react";
import { Divider } from "../../divider/Divider";
import { FieldError } from "../../fieldError/FieldError";
import { Icon } from "../../icon/Icon";
import * as labeledStyles from "./LabeledTrigger.css";
import * as selectStyles from "./Select.css";

/**
 * TODO: Address Popover theming issue. See related ticket: https://bestjira.atlassian.net/browse/DAS-4236
 */

const POSITION = "bottom";
const LOADING_TEXT = "Loading...";
const SEARCH_PLACEHOLDER = "Search...";
const NO_RESULTS_TEXT = "No results found.";

export interface Option {
  /**
   * The label to display for the option.
   */
  label: string;
  /**
   * The value of the option.
   */
  value: string;
  /**
   * Whether the option is relevant for "select relevant" functionality.
   */
  relevant?: boolean;
  /**
   * Locks the option at its current state: the row cannot be toggled and its
   * chip renders without a close icon. For a selection the consumer does not
   * own — a membership another system holds — so the control shows it is not
   * removable rather than accepting the click and reverting it. Read by
   * `MultiSelect` only; `SingleSelect` ignores it.
   */
  disabled?: boolean;
  /**
   * Optional secondary text rendered beneath the label in the dropdown
   * list. Used by `SingleSelect` to surface contextual metadata (e.g.
   * the week range behind a season tag) without crowding the trigger.
   * Currently a no-op for `MultiSelect`.
   */
  description?: string;
}

/**
 * DropdownPosition defines the possible positions for the dropdown popover.
 */
type DropdownPosition = "top" | "bottom" | "left" | "right";

type CommandFilterFn = (
  /**
   * value to filter by
   */
  value: string,
  /**
   * search string to filter against
   */
  search: string,
  /**
   * Optional keywords to filter by
   */
  keywords?: string[],
  /**
   * Optional metadata for the option
   */
  optionMeta?: Record<string, unknown>
) => number;

/**
 * CommonSelectProps defines shared props for select components.
 */
export type SelectSize = keyof typeof selectStyles.selectTriggerSizes;

export interface CommonSelectProps {
  /**
   * The size variant of the select.
   */
  size?: SelectSize;
  /**
   *  Placeholder text when no value is selected.
   */
  placeholder?: string;
  /**
   *  Whether the select is disabled.
   */
  disabled?: boolean;
  /**
   * Position of the dropdown popover.
   */
  position?: DropdownPosition;
  /**
   * Whether the select is searchable.
   */
  searchable?: boolean;
  /**
   *  Placeholder text for the search input.
   */
  searchPlaceholder?: string;
  /**
   * Text to display when no results are found.
   */
  noResultsText?: string;
  /**
   * Whether the select is in a loading state.
   */
  loading?: boolean;
  /**
   * Text to display while loading.
   */
  loadingText?: string;
  /**
   * Whether to use the default filtering logic.
   */
  shouldFilter?: boolean;
  /**
   * Custom filter function for options.
   */
  filter?: CommandFilterFn;
  /**
   * Handler for key down events on the search input.
   */
  handleInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Handler for key down events on the trigger. Invoked after the built-in
   * Enter/Space toggle; use to implement type-to-search (seed the search
   * input and open the popover on a printable key).
   */
  onTriggerKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /**
   * Controlled value of the search input. When provided together with
   * `onSearchValueChange`, the parent owns the search state (required for
   * type-to-search seeding).
   */
  searchValue?: string;
  /**
   * Change handler for the controlled search input value.
   */
  onSearchValueChange?: (value: string) => void;
  /**
   * Error message to display below the select.
   */
  error?: string | boolean;
  /**
   * Filter-bar variant. When set, the trigger displays the label as an
   * eyebrow above the selected value — keeping the filter name visible
   * even when several filters sit side-by-side. Also drives the trigger's
   * `aria-label` and the popover's `aria-label`.
   */
  label?: string;
  /**
   * Render the dropdown as a modal Radix popover. Required when the select
   * lives inside a modal Dialog — the dialog's scroll lock otherwise
   * swallows wheel events on the portalled option list, making it
   * unscrollable with the mouse. Default: false.
   *
   * Trade-off: the modal popover blocks outside pointer events while open,
   * so the first click on another control only dismisses the list.
   */
  modalPopover?: boolean;
}

/**
 * SelectBaseProps defines props for the SelectBase component.
 */
interface SelectBaseProps extends CommonSelectProps {
  /**
   * Whether the popover is open.
   */
  isPopoverOpen: boolean;
  /**
   * Content to render in the trigger button.
   */
  triggerContent: ReactNode;
  /**
   * Content to render inside the popover.
   */
  popoverContent: ReactNode;

  /**
   * Function to set the popover open state.
   */
  setIsPopoverOpen: (open: boolean) => void;
  /**
   * Handler to toggle the popover open/close.
   */
  handleTogglePopover: () => void;
  /**
   * Optional content to render in the popover footer.
   */
  footerContent?: ReactNode;
  /**
   *  Whether to show the clear all items option.
   */
  clearAll?: boolean;
  /**
   * Callback to clearAllSelection.
   */
  clearAllSelection?: () => void;
  /**
   * Action icons rendered inside the trigger, between the value and the chevron.
   * Clicking one closes the popover rather than toggling it.
   */
  triggerActions?: ReactNode;
  /**
   * Keyboard handler attached in the capture phase on the popover content.
   * Used to drive shared list navigation before cmdk/the input react.
   */
  onContentKeyDownCapture?: (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => void;
  /**
   * `id` for the option list. Links the search input (`aria-controls`) to the
   * listbox and namespaces the option `id`s used by `aria-activedescendant`.
   */
  listId?: string;
  /**
   * `id` of the keyboard-active option, surfaced via the input's
   * `aria-activedescendant`.
   */
  activeDescendantId?: string;
}

export function SelectBase({
  disabled,
  isPopoverOpen,
  loading = false,
  shouldFilter = true,
  searchable = true,
  position = POSITION,
  loadingText = LOADING_TEXT,
  searchPlaceholder = SEARCH_PLACEHOLDER,
  noResultsText = NO_RESULTS_TEXT,
  triggerContent,
  popoverContent,
  footerContent,
  clearAll = false,
  filter,
  handleTogglePopover,
  setIsPopoverOpen,
  handleInputKeyDown,
  onTriggerKeyDown,
  searchValue,
  onSearchValueChange,
  clearAllSelection,
  triggerActions,
  onContentKeyDownCapture,
  listId,
  activeDescendantId,
  size = "sm",
  error,
  label,
  modalPopover = false,
  placeholder
}: SelectBaseProps): React.ReactElement {
  const controlledSearch =
    searchValue !== undefined && onSearchValueChange !== undefined;
  const isLabeled = label !== undefined;
  // An unlabeled select still names its field in the placeholder, so the
  // icon-only clear button has a name either way.
  const fieldName = label ?? placeholder;
  return (
    <FieldError error={error}>
      {/* Root Popover: Controls open/close state and modal behavior */}
      <RadixPopover.Root
        open={isPopoverOpen}
        onOpenChange={disabled ? undefined : setIsPopoverOpen}
        modal={modalPopover}
      >
        {/* Trigger Button: Opens/closes the popover */}
        <RadixPopover.Trigger asChild>
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={disabled ? undefined : handleTogglePopover}
            className={clsx(
              selectStyles.selectTrigger,
              selectStyles.selectTriggerSizes[size],
              isLabeled && [
                labeledStyles.labeledTrigger,
                labeledStyles.labeledTriggerSizes[size]
              ],
              error && "inputError"
            )}
            data-disabled={disabled || undefined}
            aria-expanded={isPopoverOpen}
            aria-invalid={error ? true : undefined}
            aria-label={label}
            onKeyDown={(event) => {
              if (disabled) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleTogglePopover();
                return;
              }
              onTriggerKeyDown?.(event);
            }}
          >
            {/* Trigger Content (selected value or placeholder) */}
            {triggerContent}
            {/* Chevron Icon and action icons */}
            <div className={selectStyles.selectButtonLayout}>
              {triggerActions && (
                <div
                  className={selectStyles.triggerActionsContainer}
                  onClick={(e) => {
                    e.stopPropagation();
                    // An action may open a dialog; the list must not cover it.
                    setIsPopoverOpen(false);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {triggerActions}
                </div>
              )}
              {/* A disabled select offers no action: no clear, no chevron. */}
              {clearAll && !disabled && (
                <>
                  <span
                    onClick={(event) => {
                      event.stopPropagation();
                      clearAllSelection?.();
                    }}
                    className={selectStyles.closeButtonWrapper}
                    aria-label={fieldName ? `Clear ${fieldName}` : undefined}
                    role={fieldName ? "button" : undefined}
                  >
                    <Icon name="close" />
                  </span>
                  <div className={selectStyles.separator}>
                    <Divider orientation="vertical" />
                  </div>
                </>
              )}
              {!disabled && (
                <div className={selectStyles.chevronWrapper}>
                  <Icon name={isPopoverOpen ? "chevron-up" : "chevron-down"} />
                </div>
              )}
            </div>
          </div>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          {/* Popover Content: Dropdown panel */}
          <RadixPopover.Content
            align="start"
            side={position}
            sideOffset={5}
            className={clsx(
              selectStyles.selectPopover,
              selectStyles.selectPortalRoot
            )}
            onEscapeKeyDown={() => setIsPopoverOpen(false)}
            onKeyDownCapture={onContentKeyDownCapture}
            role="dialog"
            aria-label={isLabeled ? `${label} filter` : "Select dropdown"}
          >
            {/* Command Pattern: Handles search/filter/list. The highlight is
                owned by the caller's keyboard handler (onContentKeyDownCapture
                + data-active); cmdk's own `aria-selected` highlight is unstyled. */}
            <Command shouldFilter={shouldFilter} filter={filter}>
              {/* Search Input (if enabled and not loading) */}
              {searchable && !loading && (
                <div className={selectStyles.selectSearchWrapper}>
                  <Icon name="search" />
                  <Command.Input
                    className={selectStyles.selectSearchInput}
                    placeholder={searchPlaceholder}
                    autoFocus
                    onKeyDown={handleInputKeyDown}
                    aria-controls={listId}
                    aria-activedescendant={activeDescendantId}
                    {...(controlledSearch && {
                      value: searchValue,
                      onValueChange: onSearchValueChange
                    })}
                  />
                </div>
              )}
              {/* List of options */}
              <Command.List
                id={listId}
                className={selectStyles.selectList}
                role="listbox"
              >
                {loading ? (
                  // Loading State
                  <Command.Loading
                    className={clsx(
                      selectStyles.selectListSecondaryState,
                      selectStyles.selectListSecondaryStateSizes[size]
                    )}
                  >
                    {loadingText}
                  </Command.Loading>
                ) : (
                  <>
                    {/* Empty State (no results) */}
                    <Command.Empty>
                      <div
                        className={clsx(
                          selectStyles.selectListSecondaryState,
                          selectStyles.selectListSecondaryStateSizes[size]
                        )}
                      >
                        {noResultsText}
                      </div>
                    </Command.Empty>
                    {/* Actual options/content */}
                    {popoverContent}
                  </>
                )}
              </Command.List>
              {/* Optional Footer Content */}
              {footerContent && (
                <div className={selectStyles.selectFooterAction}>
                  {footerContent}
                </div>
              )}
            </Command>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    </FieldError>
  );
}
